import React, { useEffect, useRef, useState } from "react";
import {
  Empty, Input, Select, Space, Table, Tooltip,
} from "antd";
import { toast } from "react-toastify";
import { fetchEsvRetestActions, markEsvRetestActionRead, updateEsvRetestActionDataOps } from "../../../actions/dashboard";
import { loadDataOpsAssigneeSelectOptions } from "../DataOpsAnalytics/utils/utils";
import { getUserList } from "../../../actions/sendReports";
import { getUser } from "../../../utils/helpers";
import "../QC/QCFilterBar.css";
import EsvActionDetailModal from "./EsvActionDetailModal";
import {
  ESV_CLEARANCE_FILTER_OPTIONS,
  EsvAssignedToCell,
  EsvDataOpsStatusCell,
  EsvReadStatusCell,
  canAssignEsvAction,
  canEditEsvReadStatus,
  canEditEsvStatus,
  mergeEsvAssignedToRow,
  normalizeEsvClearance,
} from "./EsvDataOpsCell";
import EsvBulkActions, { EsvRowActions } from "./EsvBulkActions";
import {
  actionLabelFor,
  displayCell,
  formatActionAt,
  isActionRead,
  readStatusTooltip,
} from "./esvActionDisplay";
import "./EsvRetestActions.css";

const DEFAULT_PAGE_SIZE = 10;

const READ_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "read", label: "Read" },
];

const withTooltip = (label, title) => {
  if (title == null || title === "" || title === "-") return label;
  return <Tooltip title={title}><span>{label}</span></Tooltip>;
};

export default function EsvRetestActions({
  customerId, customerAccountId, dateRange, onUnreadCountChange,
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [listVersion, setListVersion] = useState(0);
  const [workorderDraft, setWorkorderDraft] = useState("");
  const [workorderId, setWorkorderId] = useState("");
  const [readStatus, setReadStatus] = useState("");
  const [userNames, setUserNames] = useState({});
  const [markingId, setMarkingId] = useState(null);
  const [markingBulk, setMarkingBulk] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailId, setDetailId] = useState(null);
  const [clearanceFilter, setClearanceFilter] = useState("");
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(true);
  const [savingStatusIds, setSavingStatusIds] = useState({});
  const assignInFlightRef = useRef(new Set());
  const statusInFlightRef = useRef(new Set());
  const readInFlightRef = useRef(false);
  const actionRefreshRef = useRef(false);
  const [actionLoading, setActionLoading] = useState(false);
  const tableAreaRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(undefined);
  const canAssign = canAssignEsvAction();
  const canEditReadStatus = canEditEsvReadStatus();
  const canEditStatus = canEditEsvStatus();
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  const unreadCountRef = useRef(null);
  onUnreadCountChangeRef.current = onUnreadCountChange;

  useEffect(() => {
    let active = true;
    const loadUsers = async () => {
      const response = await getUserList();
      if (!active || !response?.success || !Array.isArray(response.results)) return;
      const names = {};
      response.results.forEach((item) => {
        if (item?.id == null) return;
        names[item.id] = `${item.first_name || ""} ${item.last_name || ""}`.trim();
      });
      setUserNames(names);
    };
    const loadAssignees = async () => {
      const response = await loadDataOpsAssigneeSelectOptions();
      if (!active) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load Data Ops users");
        setAssigneeOptions([]);
      } else {
        setAssigneeOptions(response.options || []);
      }
      setAssigneeLoading(false);
    };
    loadUsers();
    loadAssignees();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadActions = async () => {
      const fromAction = actionRefreshRef.current;
      if (!fromAction) setLoading(true);
      setSelectedRowKeys([]);
      const params = {
        customer_id: customerAccountId,
        date_range: dateRange,
        page,
        page_size: pageSize,
      };
      if (workorderId) {
        params.search = workorderId;
      }
      if (readStatus === "read" || readStatus === "unread") {
        params.status = readStatus;
      }
      if (clearanceFilter === "pending" || clearanceFilter === "cleared") {
        params.dataops_status = clearanceFilter;
      }

      const response = await fetchEsvRetestActions(params, controller.signal);
      if (!active) return;

      if (response.canceled) {
        if (!fromAction) setLoading(false);
        return;
      }

      if (response.success === false) {
        setRows([]);
        setTotal(0);
        toast.error(response.message || "Failed to fetch ESV retest actions");
        setLoading(false);
        if (fromAction) {
          actionRefreshRef.current = false;
          setActionLoading(false);
          setMarkingId(null);
          setMarkingBulk(false);
          statusInFlightRef.current.clear();
          setSavingStatusIds({});
        }
        return;
      }

      const nextRows = Array.isArray(response.results) ? response.results : [];
      setRows(nextRows);
      setTotal(Number(response.count) || 0);
      setDetailId((currentId) => (
        currentId != null && nextRows.some((row) => row.id === currentId) ? currentId : null
      ));
      if (response.unread_count != null && response.unread_count !== "" && onUnreadCountChangeRef.current) {
        unreadCountRef.current = Number(response.unread_count);
        onUnreadCountChangeRef.current(customerId, response.unread_count);
      }
      setLoading(false);
      if (fromAction) {
        actionRefreshRef.current = false;
        setActionLoading(false);
        setMarkingId(null);
        setMarkingBulk(false);
        statusInFlightRef.current.clear();
        setSavingStatusIds({});
      }
    };

    loadActions();
    return () => {
      active = false;
      controller.abort();
    };
  }, [customerId, customerAccountId, dateRange, page, pageSize, workorderId, readStatus, clearanceFilter, listVersion]);

  useEffect(() => {
    const tableArea = tableAreaRef.current;
    if (!tableArea) return undefined;

    const updateTableScrollY = () => {
      const tableHead = tableArea.querySelector(".ant-table-thead");
      const pagination = tableArea.querySelector(".ant-table-pagination");
      const overhead = (tableHead?.offsetHeight ?? 55) + (pagination?.offsetHeight ?? 48);
      const nextScrollY = Math.max(120, tableArea.clientHeight - overhead);
      setTableScrollY((prev) => (prev === nextScrollY ? prev : nextScrollY));
    };

    updateTableScrollY();
    const resizeObserver = new ResizeObserver(updateTableScrollY);
    resizeObserver.observe(tableArea);
    return () => resizeObserver.disconnect();
  }, [loading, rows.length, total]);

  const applyWorkorderFilter = (value) => {
    const nextValue = String(value || "").trim();
    setWorkorderDraft(nextValue);
    if (nextValue === workorderId) return;
    setWorkorderId(nextValue);
    setPage(1);
  };

  const handleReadStatusChange = (value) => {
    setReadStatus(value || "");
    setPage(1);
  };

  const handleClearanceFilterChange = (value) => {
    setClearanceFilter(value ?? "");
    setPage(1);
  };

  const trackFieldSaving = (inFlightRef, setSavingIds, id, active) => {
    if (active) {
      inFlightRef.current.add(id);
      setSavingIds((current) => ({ ...current, [id]: true }));
      return;
    }
    inFlightRef.current.delete(id);
    setSavingIds((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const handleAssignTo = async (record, assignedTo, assignedToName) => {
    if (!canAssign || assignInFlightRef.current.has(record.id)) {
      return { success: false };
    }
    assignInFlightRef.current.add(record.id);
    const response = await updateEsvRetestActionDataOps({
      id: record.id,
      user_id: getUser()?.id,
      assigned_to: assignedTo ?? null,
    });
    assignInFlightRef.current.delete(record.id);
    if (response.canceled || response.success === false) {
      toast.error(response.message || "Failed to update assignee");
      return { success: false };
    }
    setRows((currentRows) => currentRows.map((row) => (
      row.id === record.id
        ? mergeEsvAssignedToRow(row, assignedTo, assignedToName, response)
        : row
    )));
    return { success: true };
  };

  const handleDataOpsStatus = async (record, dataopsStatus) => {
    if (!canEditStatus || statusInFlightRef.current.has(record.id)) {
      return { success: false };
    }
    trackFieldSaving(statusInFlightRef, setSavingStatusIds, record.id, true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await updateEsvRetestActionDataOps({
      id: record.id,
      user_id: getUser()?.id,
      dataops_status: dataopsStatus,
    });
    if (response.canceled || response.success === false) {
      trackFieldSaving(statusInFlightRef, setSavingStatusIds, record.id, false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update Data Ops status");
      return { success: false };
    }
    setListVersion((version) => version + 1);
    return { success: true };
  };

  const handlePageChange = (nextPage, nextPageSize) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  };

  const handleToggleRead = async (record) => {
    if (!canEditReadStatus || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    const markRead = !isActionRead(record.is_read);
    const userId = getUser()?.id;
    setMarkingId(record.id);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await markEsvRetestActionRead({
      id: record.id,
      user_id: userId,
      is_read: markRead ? 1 : 0,
    });
    readInFlightRef.current = false;
    if (response.canceled || response.success === false) {
      setMarkingId(null);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update read status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const handleBulkRead = async (markRead) => {
    const targets = rows.filter((row) => (
      selectedRowKeys.includes(row.id) && isActionRead(row.is_read) !== markRead
    ));
    if (!canEditReadStatus || !targets.length || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    setMarkingBulk(true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const userId = getUser()?.id;
    const selectedIds = targets.map((row) => row.id);
    const response = await markEsvRetestActionRead({
      id: selectedIds,
      user_id: userId,
      is_read: markRead ? 1 : 0,
    });
    readInFlightRef.current = false;
    if (response.canceled || response.success === false) {
      setMarkingBulk(false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update read status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const handleBulkDataOps = async (dataopsStatus) => {
    const targets = rows.filter((row) => (
      selectedRowKeys.includes(row.id)
      && normalizeEsvClearance(row.dataops_status ?? row.clearance) !== dataopsStatus
    ));
    if (!canEditStatus || !targets.length || readInFlightRef.current || markingBulk) return;
    readInFlightRef.current = true;
    setMarkingBulk(true);
    actionRefreshRef.current = true;
    setActionLoading(true);
    const response = await updateEsvRetestActionDataOps({
      id: targets.map((row) => row.id),
      user_id: getUser()?.id,
      dataops_status: dataopsStatus,
    });
    readInFlightRef.current = false;
    if (response.canceled || response.success === false) {
      setMarkingBulk(false);
      actionRefreshRef.current = false;
      setActionLoading(false);
      toast.error(response.message || "Failed to update Data Ops status");
      return;
    }
    setListVersion((version) => version + 1);
  };

  const selectedRows = rows.filter((row) => selectedRowKeys.includes(row.id));
  const marking = markingId != null || markingBulk;

  const columns = [
    {
      title: "WO ID",
      dataIndex: "workorder_id",
      key: "workorder_id",
      width: 120,
      render: displayCell,
    },
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      width: 150,
      render: displayCell,
    },
    {
      title: "Test",
      dataIndex: "test",
      key: "test",
      width: 180,
      render: displayCell,
    },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      width: 100,
      render: displayCell,
    },
    {
      title: "Action ESV",
      dataIndex: "action_label",
      key: "action_esv",
      width: 150,
      render: (_, record) => withTooltip(actionLabelFor(record), formatActionAt(record.action_at)),
    },
    {
      title: "Assign To",
      key: "assigned_to",
      width: 200,
      render: (_, record) => (
        <EsvAssignedToCell
          record={record}
          canEdit={canAssign}
          assigneeOptions={assigneeOptions}
          assigneeLoading={assigneeLoading}
          userNames={userNames}
          onChange={handleAssignTo}
        />
      ),
    },
    {
      title: "Data Ops Status",
      key: "dataops_status",
      width: 150,
      render: (_, record) => (
        <EsvDataOpsStatusCell record={record} />
      ),
    },
    {
      title: "Status",
      dataIndex: "is_read",
      key: "is_read",
      width: 100,
      render: (_, record) => (
        <Tooltip title={readStatusTooltip(record, userNames)}>
          <span>
            <EsvReadStatusCell record={record} />
          </span>
        </Tooltip>
      ),
    },
    ...(canEditReadStatus || canEditStatus ? [{
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      className: "EsvRetestActions__ActionsColumn",
      render: (_, record) => (
        <EsvRowActions
          record={record}
          loading={markingId === record.id || Boolean(savingStatusIds[record.id])}
          onMarkRead={handleToggleRead}
          onMarkDataOps={handleDataOpsStatus}
        />
      ),
    }] : []),
  ];

  return (
    <div className="EsvRetestActions">
      <Space className="QCFilterBar">
        <div className="field-container">
          <label>Search</label>
          <Input.Search
            allowClear
            placeholder="Search by WO ID"
            className="QCFilterSearch"
            value={workorderDraft}
            onChange={(event) => {
              const nextValue = event.target.value;
              setWorkorderDraft(nextValue);
              if (nextValue === "") {
                applyWorkorderFilter("");
              }
            }}
            onSearch={applyWorkorderFilter}
          />
        </div>
        <div className="field-container">
          <label>Status</label>
          <Select
            allowClear
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            options={READ_STATUS_OPTIONS}
            value={readStatus}
            onSelect={handleReadStatusChange}
            onClear={() => handleReadStatusChange("")}
            listHeight={400}
          />
        </div>
        <div className="field-container">
          <label>Data Ops Status</label>
          <Select
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            options={ESV_CLEARANCE_FILTER_OPTIONS}
            value={clearanceFilter}
            onChange={handleClearanceFilterChange}
            listHeight={400}
          />
        </div>
        <EsvBulkActions
          selectedCount={selectedRows.length}
          loading={markingBulk}
          canMarkRead={selectedRows.some((row) => !isActionRead(row.is_read))}
          canMarkUnread={selectedRows.some((row) => isActionRead(row.is_read))}
          canMarkPending={selectedRows.some((row) => normalizeEsvClearance(row.dataops_status ?? row.clearance) === "cleared")}
          canMarkCleared={selectedRows.some((row) => normalizeEsvClearance(row.dataops_status ?? row.clearance) === "pending")}
          onMarkRead={handleBulkRead}
          onMarkDataOps={handleBulkDataOps}
        />
      </Space>
      <div className="EsvRetestActions__Table" ref={tableAreaRef}>
        <Table
          rowKey={(record) => record.id}
          columns={columns}
          dataSource={rows}
          loading={!actionLoading && loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            columnWidth: 48,
            getCheckboxProps: () => ({
              disabled: marking,
            }),
          }}
          scroll={tableScrollY ? { y: tableScrollY } : undefined}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "50", "100"],
            onChange: handlePageChange,
          }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No ESV retest actions" />,
          }}
          onRow={(record) => ({
            onClick: (event) => {
              if (event.target.closest(".ant-table-selection-column, .ant-checkbox-wrapper, .ant-checkbox, button, .ant-btn, .ant-select, .ant-radio-group, .ant-radio-button-wrapper, .ant-spin")) {
                return;
              }
              setDetailId(record.id);
            },
            style: { cursor: "pointer" },
          })}
        />
      </div>
      <EsvActionDetailModal
        open={detailId != null}
        record={rows.find((row) => row.id === detailId) || null}
        userNames={userNames}
        markingId={markingId}
        onToggleRead={handleToggleRead}
        assigneeOptions={assigneeOptions}
        assigneeLoading={assigneeLoading}
        savingStatusIds={savingStatusIds}
        onAssignTo={handleAssignTo}
        onDataOpsStatus={handleDataOpsStatus}
        onCancel={() => setDetailId(null)}
      />
    </div>
  );
}
